import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import useBrand from './useBrand';
import useUser from './useUser';
import { useCampaign } from './useCampaign';
import useWebsites from './useWebsites';
import { useDashboard } from './useDashboard';
import {
    CurrentBrand,
    OrganizationInvitationStatus,
    OrganizationStatus,
    OrganizationTeamData,
    OrganizationUser
} from 'modules/Types';
import { OrganizationData } from 'modules/Types';
import {
    inviteOrganizationUser_api,
    createOrganization_api,
    getOrganization_api,
    deleteOrganizationBrand_api,
    deleteOrganizationUser_api,
    updateOrganizationUser_api,
    updateUserBrand_api,
    updateUserPermissionsOnBrand_api,
    createOrganizationBrand_api,
    respondOrganizationUserInvite_api,
    checkOrganizationUserInvitationStatus_api,
    updateOrganization_api,
    deleteOrganization_api,
    adminAddExistingBrandToOrganization_api,
    OrganizationUserBrandUpdate
} from 'api/organizationV2Api';
import { completeOrganizationSetup_api, adminCreateTeamWithTrial_api } from 'api/organizationApi';
import { CreateBrandPayload } from 'api/brandApi';
import { getBillingDetails_api } from 'api/paymentsV2Api';
import { OrganizationRole, PlanType, UserPermissionOnBrand } from 'modules/Const';
import { deepCopy } from 'modules/Utils';
import { CAMPAIGN_FILTER_DEFAULT } from '../pages/MarketingPage/constants';

interface OrganizationState {
    organization: OrganizationData | undefined;
    organizationUsers: OrganizationUser[] | undefined;
    organizationBrandIds: number[] | undefined;
    selectedBrandId: number | undefined | null;

    isLoadingOrganization: boolean;
    isOrganizationInitialized: boolean;

    isLoadingOrganizationAction: boolean;
    isLoadingAddBrandToOrganization: boolean;
    isLoadingDeleteMember: boolean;

    showAddBrandModal: boolean;

    invitationStatus: OrganizationInvitationStatus | undefined;
    
    billingStatus: {
        isScheduledForCancellation: boolean;
        scheduledCancellationDate: string | null;
        nextBillingDate: string | null;
        lastUpdated: string;
    } | null;
    
    showPaymentModal: boolean;

    hasOrganizationAdminAccess: () => boolean;
    hasTeamViewAccess: () => boolean;
    getIsOrganizationPaymentPastDue: () => boolean;
    getIsTrialActionable: () => boolean;
    getBillingStatus: () => Promise<void>;
    invalidateBillingStatus: () => void;
    openPaymentModal: () => void;
    closePaymentModal: () => void;

    setOrganization: (organization: OrganizationData | undefined) => void;
    setIsLoadingOrganizationAction: (isLoading: boolean) => void;
    setIsLoadingAddBrandToOrganization: (isLoading: boolean) => void;
    setInvitationStatus: (invitationStatus: OrganizationInvitationStatus) => void;
    setIsOrganizationInitialized: (isInitialized: boolean) => void;

    setShowAddBrandModal: (show: boolean) => void;
    openAddBrandModal: () => void;
    closeAddBrandModal: () => void;
    handleFinishAddingBrand: () => Promise<void>;

    setSelectedBrand: (brandId: number | undefined | null) => void;
    getSelectedBrand: () => CurrentBrand | undefined;

    resetOrganization: () => void;

    // Refresh organization campaigns and websites when needed (e.g., after creating new content)
    refreshOrganizationData: (organizationId?: number) => Promise<void>;

    getOrganization: ({
        organizationId,
        brandId,
        userId,
    }: {
        organizationId?: number;
        brandId?: number;
        userId?: number;
    }) => Promise<OrganizationTeamData | null | undefined>;

    updateOrganization: ({
        organizationId,
        name,
        setupStatus,
        profile_picture,
    }: {
        organizationId: number;
        name?: string;
        setupStatus?: OrganizationStatus;
        profile_picture?: string;
    }) => Promise<void>;

    inviteOrganizationUsers: ({
        organizationId,
        users,
        brands,
    }: {
        organizationId: number;
        users: {
            email: string;
            role: OrganizationRole;
            firstName: string;
            lastName: string;
        }[];
        brands: CurrentBrand[];
    }) => Promise<OrganizationTeamData | null | undefined>;

    respondOrganizationUserInvite: ({
        organizationId,
        userId,
        invitationStatus,
        brandIds,
    }: {
        organizationId: number;
        userId: number;
        invitationStatus: OrganizationInvitationStatus;
        brandIds?: number[];
    }) => Promise<void>;

    checkOrganizationUserInvitationStatus: ({
        organizationId,
        userId,
    }: {
        organizationId: number;
        userId: number;
    }) => Promise<OrganizationInvitationStatus | null | undefined>;

    createOrganizationBrand: ({
        profileName,
        connections,
        onFinishAddingToOrganization
    }: {
        profileName: string;
        connections: any;
        onFinishAddingToOrganization: () => void;
    }) => Promise<void>;

    updateOrganizationUser: ({
        organizationId,
        userId,
        role,
        brands,
    }: {
        organizationId: number;
        userId: number;
        role: OrganizationRole;
        brands: OrganizationUserBrandUpdate[];
    }) => Promise<void>;

    updateUserBrand: ({
        organizationId,
        brandId,
        users,
    }: {
        organizationId: number;
        brandId: number;
        users: {
            userId: number;
            permissionLevel: UserPermissionOnBrand;
        }[];
    }) => Promise<void>;

    updateUserPermissionsOnBrand: ({
        organizationId,
        brandId,
        updates,
    }: {
        organizationId: number;
        brandId: number;
        updates: {
            userId: number;
            permissionLevel: UserPermissionOnBrand | 'none';
        }[];
    }) => Promise<void>;

    deleteOrganizationBrand: ({
        organizationId,
        brandId,
    }: {
        organizationId: number;
        brandId: number;
    }) => Promise<void>;

    deleteOrganizationUser: ({
        organizationId,
        userId,
    }: {
        organizationId: number;
        userId: number;
    }) => Promise<void>;

    deleteOrganization: () => Promise<void>;

    completeOrganizationSetup: (
        name: string,
        brandIds: number[],
        emails?: string[],
        profilePictureUrl?: string
    ) => Promise<OrganizationData | null>;

    adminCreateTeamWithTrial: (
        name: string,
        ownerEmail: string,
        brandIds?: number[],
        emails?: string[],
        profilePictureUrl?: string
    ) => Promise<OrganizationData | null>;

    adminAddExistingBrandToOrganization: ({
        organizationId,
        brandId,
        existingUserPermissions,
        organizationUserPermissions
    }: {
        organizationId: number;
        brandId: number;
        existingUserPermissions?: Array<{
            userId: number;
            permissionLevel: string;
        }>;
        organizationUserPermissions?: Array<{
            userId: number;
            permissionLevel: string;
        }>;
    }) => Promise<{ organizationUsers: OrganizationUser[], brandAdded: any } | null | undefined>;
}

const useOrganizationV2 = create(
    immer<OrganizationState>((set, get) => ({
        // State
        organization: undefined,
        organizationUsers: [],
        organizationBrandIds: [],
        selectedBrandId: undefined,
        isLoadingOrganization: false,
        isOrganizationInitialized: false,

        isLoadingOrganizationAction: false,
        isLoadingAddBrandToOrganization: false,
        isLoadingDeleteMember: false,
        showAddBrandModal: false,
        invitationStatus: undefined,
        billingStatus: null,
        showPaymentModal: false,

        setOrganization: (organization: OrganizationData | undefined) => {
            set(state => { state.organization = organization })
        },
        setIsLoadingOrganizationAction: (isLoading: boolean) => set(state => { state.isLoadingOrganizationAction = isLoading }),
        setIsLoadingAddBrandToOrganization: (isLoading: boolean) => set(state => { state.isLoadingAddBrandToOrganization = isLoading }),
        setInvitationStatus: (invitationStatus: OrganizationInvitationStatus) => set(state => { state.invitationStatus = invitationStatus }),
        setIsOrganizationInitialized: (isInitialized: boolean) => set(state => { state.isOrganizationInitialized = isInitialized }),

        setShowAddBrandModal: (show: boolean) => set(state => { state.showAddBrandModal = show }),
        openAddBrandModal: () => set(state => { state.showAddBrandModal = true }),
        closeAddBrandModal: () => set(state => { state.showAddBrandModal = false }),

        handleFinishAddingBrand: async () => {
            const { organization, closeAddBrandModal, refreshOrganizationData } = get();
            closeAddBrandModal();

            // Refresh organization data to show new brand in both team and dashboard views
            if (organization?.id) {
                await refreshOrganizationData(organization.id);
            }
        },

        setSelectedBrand: (brandId: number | undefined | null) => set(state => { state.selectedBrandId = brandId }),
        getSelectedBrand: () => {
            const { loadedBrandsDict } = useBrand.getState();
            const { selectedBrandId } = get();
            return selectedBrandId ? loadedBrandsDict?.[selectedBrandId] : undefined;
        },

        resetOrganization: () => {
            const { organization } = get();
            set(state => {
                state.organization = undefined;
                state.organizationUsers = [];
                state.organizationBrandIds = [];
                state.selectedBrandId = undefined;
                state.invitationStatus = undefined;
            })
        },

        hasOrganizationAdminAccess: (): boolean => {
            const { organization, organizationUsers } = get();
            const { currentUser, isSymphonyAdmin } = useUser.getState();
            if (!organization) {
                return false;
            }
            if (isSymphonyAdmin) {
                return true;
            }
            if (organization.owner_id === currentUser?.id) {
                return true;
            }
            const role = organizationUsers?.find(ou => ou.user_id === currentUser?.id)?.role || OrganizationRole.MEMBER;
            return role === OrganizationRole.ADMINISTRATOR || role === OrganizationRole.OWNER
        },

        /** Checks if current user has access to team view based on their organization role */
        hasTeamViewAccess: (): boolean => {
            const { organization, organizationUsers } = get();
            const { currentUser, isSymphonyAdmin } = useUser.getState();

            // No organization means no team view access
            if (!organization) {
                return false;
            }

            // Symphony admins always have access
            if (isSymphonyAdmin) {
                return true;
            }

            // Organization owners always have access
            if (organization.owner_id === currentUser?.id) {
                return true;
            }

            // Check user's role in the organization - only admins and owners can access team view
            const role = organizationUsers?.find(ou => ou.user_id === currentUser?.id)?.role || OrganizationRole.MEMBER;
            return role === OrganizationRole.ADMINISTRATOR || role === OrganizationRole.OWNER;
        },

        getIsOrganizationPaymentPastDue: (): boolean => {
            const { organization } = get();
            console.log('organization', organization)
            // Check if organization has team/team_v2 subscription and is past due or paused
            return Boolean(
                (organization?.stripe_subscription_status === 'past_due' || organization?.stripe_subscription_status === 'paused') &&
                organization?.stripe_subscription_id
            );
        },

        getIsTrialActionable: (): boolean => {
            const { organization, billingStatus } = get();
            
            // Don't show trial banner if subscription is scheduled for cancellation
            if (billingStatus?.isScheduledForCancellation) {
                return false;
            }
            
            // Don't show if subscription is already cancelled
            if (organization?.stripe_subscription_status === 'canceled') {
                return false;
            }
            
            return true;
        },

        getBillingStatus: async (): Promise<void> => {
            const { organization } = get();
            if (!organization?.id) return;
            
            try {
                // Lightweight call - just billing status, not full details
                const billingData = await getBillingDetails_api({
                    organizationId: organization.id,
                    includeUsageData: false // Keep it lightweight
                });
                
                set(state => {
                    state.billingStatus = {
                        isScheduledForCancellation: !!billingData.scheduledCancellationDate,
                        scheduledCancellationDate: billingData.scheduledCancellationDate || null,
                        nextBillingDate: billingData.nextBillingDate || null,
                        lastUpdated: new Date().toISOString()
                    };
                });
            } catch (error) {
                console.error('Error fetching billing status:', error);
            }
        },

        invalidateBillingStatus: (): void => {
            set(state => {
                state.billingStatus = null;
            });
        },

        openPaymentModal: (): void => {
            set(state => {
                state.showPaymentModal = true;
            });
        },

        closePaymentModal: (): void => {
            set(state => {
                state.showPaymentModal = false;
            });
        },

        // Refresh organization campaigns and websites when needed
        refreshOrganizationData: async (organizationId?: number): Promise<void> => {
            const { organization, getOrganization } = get();
            const orgId = organizationId || organization?.id;

            if (orgId) {
                const { getOrganizationCampaigns, getOrganizationCampaignDrafts } = useCampaign.getState();
                const { getOrganizationWebsites } = useWebsites.getState();
                const { getOrganizationDashboard, selectedDate } = useDashboard.getState();

                // Refresh campaigns and websites
                await Promise.all([
                    getOrganization({ organizationId: orgId }),
                    getOrganizationDashboard({
                        organizationId: orgId,
                        startDate: selectedDate.startDate,
                        endDate: selectedDate.endDate
                    }),

                    getOrganizationCampaigns({
                        organizationId: orgId,
                        params: { ...CAMPAIGN_FILTER_DEFAULT, limit: 50 },
                        isAnalytics: true
                    }),
                    getOrganizationCampaignDrafts({
                        organizationId: orgId,
                        params: { ...CAMPAIGN_FILTER_DEFAULT, limit: 50 }
                    }),
                    getOrganizationWebsites({ organizationId: orgId })
                ]).catch(error => {
                    console.error('Error refreshing organization data:', error);
                });
            }
        },

        getOrganization: async ({
            organizationId,
            brandId,
            userId,
        }: {
            organizationId?: number;
            brandId?: number;
            userId?: number;
        }): Promise<OrganizationTeamData | null | undefined> => {
            const { resetOrganization, getBillingStatus } = get();
            const { isSymphonyAdmin, currentUser } = useUser.getState();
            const { updateOrAddBrands, loadedBrandsDict } = useBrand.getState();

            set({ isLoadingOrganization: true });
            try {
                const res = await getOrganization_api({
                    organizationId,
                    brandId,
                    userId,
                });

                if (res) {
                    const invitationStatus = res.organizationUsers.find(ou => ou.user_id === userId)?.invitation_status;

                    // Check if this is an admin accessing via admin team view
                    const isAdminAccess = isSymphonyAdmin && !userId;

                    // organization.brands is now the single source of truth (includes user permissions)
                    const organizationBrands = res.organization.brands || [];
                    const filteredBrands = organizationBrands.filter(brand => !loadedBrandsDict?.[brand.id])

                    // Update useBrand store with organization brands (permission-filtered)
                    // Allow for both accepted invitations and admin access
                    if (filteredBrands.length > 0 && (invitationStatus === OrganizationInvitationStatus.ACCEPTED || isAdminAccess)) {
                        updateOrAddBrands(filteredBrands);
                    }

                    // Note: For organization users, currentBrand is intentionally not set here
                    // This keeps them in "team view" until they explicitly select a brand

                    set({
                        invitationStatus,
                        organization: res.organization, // Now includes brands field with user permissions
                        organizationUsers: res.organizationUsers,
                        organizationBrandIds: organizationBrands.map(brand => brand.id),
                    });

                    // Load organization campaigns and websites once after successful organization load
                    // Allow for both accepted invitations and admin access
                    if ((invitationStatus === OrganizationInvitationStatus.ACCEPTED || isAdminAccess) && res.organization.id) {
                        const { getOrganizationCampaigns, getOrganizationCampaignDrafts } = useCampaign.getState();
                        const { getOrganizationWebsites } = useWebsites.getState();

                        // Load campaigns, websites, and billing status in background (don't await to avoid blocking)
                        Promise.all([
                            getOrganizationCampaigns({
                                organizationId: res.organization.id,
                                params: { ...CAMPAIGN_FILTER_DEFAULT, limit: 50 }, // Load more initially for better caching
                                isAnalytics: true
                            }),
                            getOrganizationCampaignDrafts({
                                organizationId: res.organization.id,
                                params: { ...CAMPAIGN_FILTER_DEFAULT, limit: 50 }
                            }),
                            getOrganizationWebsites({ organizationId: res.organization.id }),
                            getBillingStatus() // Load billing status for trial banner logic
                        ]).catch(error => {
                            console.error('Error loading organization data in background:', error);
                        });
                    }
                } else {
                    console.log('HOOK: resetOrganization in getOrganization')
                    resetOrganization();
                }

                return res;
            } catch (error) {
                console.error('Failed to fetch organization data:', error);
                return null;
            } finally {
                set({
                    isOrganizationInitialized: true,
                    isLoadingOrganization: false,
                });
            }
        },

        updateOrganization: async ({
            organizationId,
            name,
            setupStatus,
            profile_picture,
        }: {
            organizationId: number;
            name?: string;
            setupStatus?: OrganizationStatus;
            profile_picture?: string;
        }): Promise<void> => {
            set({ isLoadingOrganization: true });
            try {
                const res = await updateOrganization_api({
                    organizationId,
                    name,
                    setupStatus,
                    profile_picture,
                });

                if (res) {
                    set({
                        organization: res.organization,
                    });
                }
            } catch (error) {
                console.error('Failed to update organization:', error);
            } finally {
                set({ isLoadingOrganization: false });
            }
        },

        inviteOrganizationUsers: async ({
            organizationId,
            users,
            brands,
        }: {
            organizationId: number;
            users: {
                email: string;
                role: OrganizationRole;
                firstName: string;
                lastName: string;
            }[];
            brands: CurrentBrand[];
        }): Promise<OrganizationTeamData | null | undefined> => {
            set({ isLoadingOrganizationAction: true });
            try {
                const res = await inviteOrganizationUser_api({
                    organizationId,
                    users,
                    brands,
                });

                if (res) {
                    set({
                        organizationUsers: res.organizationUsers,
                    });
                }

                return res;
            } catch (error) {
                console.error('Failed to add organization user:', error);
                return null;
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        respondOrganizationUserInvite: async ({
            organizationId,
            userId,
            invitationStatus,
            brandIds,
        }: {
            organizationId: number;
            userId: number;
            invitationStatus: OrganizationInvitationStatus;
            brandIds?: number[];
        }): Promise<void> => {
            const { updateOrAddBrands, getBrands } = useBrand.getState();
            set({ isLoadingOrganizationAction: true });

            try {
                const res = await respondOrganizationUserInvite_api({
                    organizationId,
                    userId,
                    invitationStatus,
                    brandIds,
                });

                if (res) {
                    const brandsToAdd = res.organization.brands || [];
                    updateOrAddBrands(brandsToAdd);
                    set({
                        invitationStatus,
                        organizationUsers: res.organizationUsers,
                        organizationBrandIds: res.organization.brands?.map(brand => brand.id) || [],
                    });

                    await getBrands();
                }
            } catch (error) {
                console.error('Failed to respond to organization user invite:', error);
            } finally {
                set(state => { state.isLoadingOrganizationAction = false });
            }
        },

        checkOrganizationUserInvitationStatus: async ({
            organizationId,
            userId,
        }: {
            organizationId: number;
            userId: number;
        }): Promise<OrganizationInvitationStatus | null | undefined> => {
            set({ isLoadingOrganizationAction: true });

            try {
                const res = await checkOrganizationUserInvitationStatus_api({
                    organizationId,
                    userId,
                });

                return res?.invitationStatus;
            } catch (error) {
                console.error('Failed to check organization user invitation status:', error);
                return null;
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        createOrganizationBrand: async ({
            profileName,
            connections,
            onFinishAddingToOrganization
        }: {
            profileName: string;
            connections: any;
            onFinishAddingToOrganization: () => void;
        }): Promise<void> => {
            const { createBrand } = useBrand.getState();
            const { organization } = get();

            if (!organization) return;

            // Infer brand name from connections or use provided name
            let brandName = profileName.trim();

            if (!brandName) {
                if (connections.spotify) {
                    brandName = connections.spotify.name;
                } else if (connections.tiktok) {
                    brandName = connections.tiktok.username;
                } else if (connections.instagram) {
                    brandName = connections.instagram.username;
                }
            }

            if (!brandName) return;

            set({ isLoadingOrganizationAction: true });
            try {
                const customization: any = {};

                if (connections.spotify) {
                    customization.spotify_artist = connections.spotify;
                }
                if (connections.tiktok) {
                    customization.tiktok = connections.tiktok;
                }
                if (connections.instagram) {
                    customization.instagram_profile_for_onboarding = connections.instagram;
                }

                const brandPayload: CreateBrandPayload = {
                    name: brandName,
                    customization,
                    tier: PlanType.TEAM_V2,
                    organizationId: organization.id
                };

                await createBrand(brandPayload);
                onFinishAddingToOrganization();
            } catch (error) {
                console.error('Failed to create organization brand:', error);
                throw error;
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        updateOrganizationUser: async ({
            organizationId,
            userId,
            role,
            brands,
        }: {
            organizationId: number;
            userId: number;
            role: OrganizationRole;
            brands: OrganizationUserBrandUpdate[];
        }): Promise<void> => {
            set({ isLoadingOrganizationAction: true });
            try {
                const { organizationUsers: organizationUsersLocal } = await updateOrganizationUser_api({
                    organizationId,
                    userId,
                    role,
                    brands,
                });

                set({
                    organizationUsers: organizationUsersLocal,
                });
            } catch (error) {
                console.error('Failed to update organization user:', error);
                throw error; // Re-throw to allow proper error handling in components
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        updateUserBrand: async ({
            organizationId,
            brandId,
            users,
        }: {
            organizationId: number;
            brandId: number;
            users: {
                userId: number;
                permissionLevel: UserPermissionOnBrand;
            }[];
        }): Promise<void> => {
            set({ isLoadingOrganizationAction: true });
            try {
                const { organizationUsers: organizationUsersLocal } = await updateUserBrand_api({
                    organizationId,
                    brandId,
                    users,
                });

                set({
                    organizationUsers: organizationUsersLocal,
                });
            } catch (error) {
                console.error('Failed to update user brand:', error);
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        updateUserPermissionsOnBrand: async ({
            organizationId,
            brandId,
            updates,
        }: {
            organizationId: number;
            brandId: number;
            updates: {
                userId: number;
                permissionLevel: UserPermissionOnBrand | 'none';
            }[];
        }): Promise<void> => {
            set({ isLoadingOrganizationAction: true });
            try {
                const { organizationUsers: organizationUsersLocal } = await updateUserPermissionsOnBrand_api({
                    organizationId,
                    brandId,
                    updates,
                });

                set({
                    organizationUsers: organizationUsersLocal,
                });

                // Refresh the organization data to get updated user-brand relationships
                const { getOrganization } = get();
                await getOrganization({ organizationId });
            } catch (error) {
                console.error('Failed to update user permissions on brand:', error);
                throw error; // Re-throw so the UI can handle the error
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        deleteOrganizationBrand: async ({
            organizationId,
            brandId,
        }: {
            organizationId: number;
            brandId: number;
        }): Promise<void> => {
            const { organizationBrandIds } = get();
            const { isSymphonyAdmin } = useUser.getState();
            const { updateOrAddBrand, loadedBrandsDict, getBrands, removeBrand } = useBrand.getState();
            const myBrandWasRemoved = loadedBrandsDict?.[brandId]?.permissionLevel === UserPermissionOnBrand.OWNER;

            set({ isLoadingOrganizationAction: true });
            try {
                const res = await deleteOrganizationBrand_api({
                    organizationId,
                    brandId,
                });

                if (res) {
                    set({
                        organizationUsers: res.organizationUsers,
                        organizationBrandIds: organizationBrandIds?.filter(id => id !== brandId) || [],
                    });
                }
                const brand = deepCopy(loadedBrandsDict?.[brandId]);
                if (brand) {
                    if (myBrandWasRemoved) {
                        brand.tier = PlanType.FREE;
                        updateOrAddBrand(brand);
                    } else {
                        // if the brand removed is not my brand, remove it from local state
                        removeBrand(brandId);
                    }
                }

                await getBrands();
            } catch (error) {
                console.error('Failed to remove organization brand:', error);
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        deleteOrganizationUser: async ({
            organizationId,
            userId,
        }: {
            organizationId: number;
            userId: number;
        }): Promise<void> => {
            const { getOrganization } = get();
            const { getBrands, currentBrand } = useBrand.getState();
            const { currentUser } = useUser.getState();
            const iLeftTheTeam = currentUser?.id === userId;

            set({ isLoadingDeleteMember: true });

            try {
                await deleteOrganizationUser_api({
                    organizationId,
                    userId,
                });

                if (iLeftTheTeam) {
                    // If I left the team, clear organization state and refresh brands
                    set({
                        invitationStatus: undefined,
                        organization: undefined,
                        organizationUsers: [],
                        organizationBrandIds: [],
                    });
                    await getBrands();
                } else {
                    // If someone else was removed, refresh organization and brands
                    await Promise.all([
                        getOrganization({
                            organizationId,
                            brandId: currentBrand?.id,
                            userId: currentUser?.id,
                        }),
                        getBrands(),
                    ]);
                }
            } catch (error) {
                console.error('Failed to remove organization user:', error);
            } finally {
                set({ isLoadingDeleteMember: false });
            }
        },

        deleteOrganization: async (): Promise<void> => {
            const { organization, resetOrganization } = get();
            const { getBrands } = useBrand.getState();
            set({ isLoadingOrganizationAction: true });
            try {
                const res = await deleteOrganization_api({
                    organizationId: organization?.id!,
                });

                if (res) {
                    resetOrganization();
                    getBrands();
                }
            } catch (error) {
                console.error('Failed to delete organization:', error);
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        completeOrganizationSetup: async (name: string, brandIds: number[], emails: string[] = [], profilePictureUrl?: string) => {
            const { organization } = get();
            const { getBrands } = useBrand.getState();

            if (!organization?.id) {
                console.error('No organization ID available for setup completion');
                return null;
            }

            set({ isLoadingOrganizationAction: true });

            try {
                const res = await completeOrganizationSetup_api(organization.id, name, brandIds, emails, profilePictureUrl);
                const { organization: organizationLocal } = res;

                if (organizationLocal) {
                    set((state) => {
                        state.organization = organizationLocal;
                        state.organizationBrandIds = organizationLocal.brands?.map((brandData: CurrentBrand) => brandData.id) || [];
                    });
                }

                // Refresh brands to get updated tier information
                await getBrands(true);
                return organizationLocal;
            } catch (error) {
                console.error('Failed to complete organization setup:', error);
                return null;
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        /**
         * Admin function: Creates a team with trial subscription
         * @param name - Team name
         * @param ownerEmail - Email of the team owner
         * @param brandIds - Array of brand IDs to add to the team
         * @param emails - Array of emails to invite to the team
         * @param profilePictureUrl - Optional team profile picture URL
         * @returns Promise with organization data
         */
        adminCreateTeamWithTrial: async (
            name: string,
            ownerEmail: string,
            brandIds: number[] = [],
            emails: string[] = [],
            profilePictureUrl?: string
        ) => {
            set({ isLoadingOrganizationAction: true });

            try {
                const res = await adminCreateTeamWithTrial_api(name, ownerEmail, brandIds, emails, profilePictureUrl);
                const { organization: organizationLocal } = res;

                if (organizationLocal) {
                    // Set the created organization as current organization for admin view
                    set((state) => {
                        state.organization = organizationLocal;
                        state.organizationBrandIds = brandIds || [];
                    });
                }

                return organizationLocal;
            } catch (error) {
                console.error('Failed to create team with trial:', error);
                return null;
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },

        /**
         * Admin-only function to add an existing brand to an organization
         * This handles the complete flow: moving brand, setting permissions, sending emails
         * @param organizationId - ID of the organization to add the brand to
         * @param brandId - ID of the brand to add
         * @param existingUserPermissions - Permissions for existing brand users 
         * @param organizationUserPermissions - Permissions for organization users
         */
        adminAddExistingBrandToOrganization: async ({
            organizationId,
            brandId,
            existingUserPermissions = [],
            organizationUserPermissions = []
        }: {
            organizationId: number;
            brandId: number;
            existingUserPermissions?: Array<{
                userId: number;
                permissionLevel: string;
            }>;
            organizationUserPermissions?: Array<{
                userId: number;
                permissionLevel: string;
            }>;
        }): Promise<{ organizationUsers: OrganizationUser[], brandAdded: any } | null | undefined> => {
            set({ isLoadingOrganizationAction: true });
            try {
                const res = await adminAddExistingBrandToOrganization_api({
                    organizationId,
                    brandId,
                    existingUserPermissions,
                    organizationUserPermissions
                });

                if (res) {
                    // Update organization users with new data
                    set({
                        organizationUsers: res.organizationUsers,
                    });

                    // Refresh the organization to get updated brand list
                    const { getOrganization } = get();
                    await getOrganization({ organizationId });

                    // Refresh organization dashboard to include metrics from newly added brand
                    const { getOrganizationDashboard, selectedDate } = useDashboard.getState();
                    await getOrganizationDashboard({
                        organizationId,
                        startDate: selectedDate.startDate,
                        endDate: selectedDate.endDate
                    });
                }

                return res;
            } catch (error) {
                console.error('Failed to add existing brand to organization:', error);
                throw error; // Re-throw to allow proper error handling in components
            } finally {
                set({ isLoadingOrganizationAction: false });
            }
        },
    }))
);

export default useOrganizationV2;
